import { token } from '@atlaskit/tokens';

import { type GridPosition } from '../../../machine/state/types';
import { SELECTIONS_FOCUS_BOX_ELEMENT_ID } from '../constants';
import { getCellElementByIds } from '../utils/get-cell-element-by-ids';

// Above the Highlights (> 10)
// The Blue Border. It must sit on top of the Highlights (10), so the focus appears on top.
const FOCUS_BOX_Z_INDEX = '20';

export class FocusRenderer {
  private el: HTMLElement | null = null;

  constructor(
    private readonly renderTarget: HTMLElement,
    private readonly scrollContainer: HTMLElement
  ) {}

  /**
   * Lazily creates the focus element if it doesn't exist, then updates it
   * to match the new position.
   */
  update(position: GridPosition | null) {
    // Handle "No Focus" (e.g., Click outside or initial state)
    if (!position) {
      this.hide();

      return;
    }

    // Ensure Element Exists (Lazy Creation)
    // We don't create the DOM node until the first time we actually need it.
    if (!this.el) {
      this.el = this.#createFocusElement();
      this.renderTarget.appendChild(this.el);
    }

    // Find Target Cell in the DOM
    const cellEl = getCellElementByIds({
      entryId: position.row.id,
      fieldId: position.col.id,
    });

    // If cell isn't in DOM (e.g., virtualized out of view), we hide the focus box.
    // It will reappear automatically when the cell scrolls back into view
    // (triggered by the virtualizer or a scroll event).
    if (!cellEl) {
      this.hide();

      return;
    }

    // Calculate Geometry
    const cellRect = cellEl.getBoundingClientRect();
    const scrollRect = this.scrollContainer.getBoundingClientRect();

    /**
     * getBoundingClientRect() gives us viewport-relative coordinates, but our
     * focus element lives inside the scrollableContainer.
     *
     * Formula: (Cell Viewport Pos) - (Container Viewport Pos) + (Container Scroll Offset)
     */
    const x = cellRect.left - scrollRect.left + this.scrollContainer.scrollLeft;
    const y = cellRect.top - scrollRect.top + this.scrollContainer.scrollTop;

    // Apply Styles
    // We use transform for high-performance moving (GPU compositing).
    this.el.style.transform = `translate(${x}px, ${y}px)`;
    this.el.style.width = `${cellRect.width}px`;
    this.el.style.height = `${cellRect.height}px`;
    this.el.style.display = 'block';
  }

  /**
   * Hides the focus box without destroying the DOM node.
   * Useful when focus is lost temporarily or the target is virtualized.
   */
  hide() {
    if (this.el) {
      this.el.style.display = 'none';
    }
  }

  /**
   * Permanently removes the focus element from the DOM.
   * Call this when the effect/component is unmounting.
   */
  clear() {
    if (this.el) {
      this.el.remove();
      this.el = null;
    }
  }

  /**
   * Create the DOM node for the focus element.
   */
  #createFocusElement(): HTMLElement {
    const el = document.createElement('div');

    el.id = SELECTIONS_FOCUS_BOX_ELEMENT_ID;

    // Layout & Positioning
    el.style.position = 'absolute';
    el.style.display = 'none'; // Start hidden
    el.style.boxSizing = 'border-box';
    el.style.pointerEvents = 'none'; // Click-through to the cell below

    // Theming (Atlaskit Tokens)
    el.style.border = `2px solid ${token('color.border.selected', '#0C66E4')}`;
    el.style.borderRadius = token('radius.small', '3px');

    // Layering (The Sandwich Strategy)
    // Must be > 11 to sit on top of the cell text.
    el.style.zIndex = FOCUS_BOX_Z_INDEX;

    return el;
  }
}
